import { formatDate } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { ColDef, GridOptions } from 'ag-grid-community';
import { sortBy, uniqBy } from 'lodash';
import { finalize } from 'rxjs';
import { defaultTableDefOptions } from '../../../helpers';
import { NotifyService } from '../../../service/notify.service';
import { ReportsService } from '../../../service/reports.service';
import { UserLoginService } from '../../../service/user-login.service';
import { CaseIncident, CaseWithIncidents, ShareCasesRequest } from '../../../types';
import { FullReportResponse } from '../../../types/reports';
import { CellTheftReportActionsComponent } from '../theft-report/cell-theft-report-actions/cell-theft-report-actions.component';
import { CellTheftReportPhotoComponent } from '../theft-report/cell-theft-report-photo/cell-theft-report-photo.component';
import { CellTheftReportVideoComponent } from '../theft-report/cell-theft-report-video/cell-theft-report-video.component';
import { convertDateToTimezone } from '../../../date.utils';

@Component({
  selector: 'app-user-report',
  templateUrl: './user-report.component.html',
  styleUrls: ['./user-report.component.scss'],
  standalone: false,
})
export class UserReportComponent implements OnInit {
  private notifyService = inject(NotifyService);
  private reportService = inject(ReportsService);
  private userLoginService = inject(UserLoginService);

  public reportId = input<string>();

  public isSharing = signal<boolean>(false);
  public loadingData = signal<boolean>(true);
  public report = signal<FullReportResponse | undefined>(undefined);
  public loadErrorMessage = signal<string | undefined>(undefined);

  public subtitle = computed(() => {
    const report = this.report();
    if (!report) return '';

    return `Created on ${formatDate(report.created_at, 'medium', 'en-US')} by ${report.created_by}`;
  });

  public reportCases = computed(() => {
    const allCases = this.report()?.cases ?? [];
    const allBranches = uniqBy(allCases, (c) => c.branch_id).map((c) => c.branch_id);

    const allCasesByBranch = allBranches.map((branchId) => {
      const branchCases = allCases.filter((c) => c.branch_id === branchId);
      return {
        branchId,
        branchName: branchCases[0].branch_name,
        cases: branchCases.flatMap((c) =>
          c.incidents.map(
            (i) => ({ ...c, ...i, incident_date: convertDateToTimezone(i.incident_date, c.location_timezone) }) satisfies CaseIncident,
          ),
        ),
      };
    });

    return sortBy(allCasesByBranch, ['branchName']);
  });

  public reportLocationIds = computed(() => this.report()?.cases.map((l) => l.location_id) ?? []);

  public readonly tableDef: GridOptions<CaseIncident | CaseWithIncidents> = {
    ...defaultTableDefOptions(),
    pagination: true,
    paginationPageSize: 10,
    paginationPageSizeSelector: [10, 20, 50],
  };

  public readonly columnDefs: ColDef<CaseIncident | CaseWithIncidents>[] = [
    {
      field: 'review_id',
      headerName: 'Review ID',
      cellDataType: 'text',
      sortable: false,
      minWidth: 320,
      hide: true,
    },
    {
      field: 'location_name',
      headerName: 'Location Name',
      cellDataType: 'text',
      sortable: true,
      maxWidth: 300,
    },
    {
      field: 'case_index',
      headerName: 'Case #',
      cellDataType: 'number',
      filter: 'agNumberColumnFilter',
      sortable: true,
      maxWidth: 100,
    },
    {
      field: 'instance_num',
      headerName: 'Instance #',
      cellDataType: 'number',
      filter: 'agNumberColumnFilter',
      sortable: true,
      maxWidth: 140,
    },
    {
      field: 'date_shared',
      headerName: 'Date Shared',
      cellDataType: 'date',
      filter: 'agDateColumnFilter',
      sortable: true,
      width: 100,
      valueFormatter: (params) => (params.value ? formatDate(params.value, 'medium', 'en-US') : ''),
    },
    {
      field: 'incident_date',
      headerName: 'Incident Date',
      cellDataType: 'date',
      filter: 'agDateColumnFilter',
      sortable: true,
      width: 100,
      valueFormatter: (params) => (params.value ? formatDate(params.value, 'medium', 'en-US') : ''),
    },
    {
      headerName: 'Photo',
      sortable: false,
      cellRenderer: CellTheftReportPhotoComponent,
      maxWidth: 80,
    },
    {
      headerName: 'Video',
      sortable: false,
      cellRenderer: CellTheftReportVideoComponent,
      maxWidth: 80,
    },
    {
      cellClass: ['inline-flex', 'items-center', 'justify-end'],
      headerName: '',
      suppressKeyboardEvent: () => true,
      sortable: false,
      cellRenderer: CellTheftReportActionsComponent,
      minWidth: 140,
    },
  ];

  ngOnInit() {
    this.loadReport();
  }

  loadReport() {
    this.loadingData.set(true);
    this.loadErrorMessage.set(undefined);

    this.reportService
      .getReportById(this.reportId())
      .pipe(finalize(() => this.loadingData.set(false)))
      .subscribe({
        next: (report) => this.report.set(report),
        error: (error) => this.handleLoadError(error),
      });
  }

  private handleLoadError(error: unknown) {
    const status = error instanceof HttpErrorResponse ? error.status : undefined;

    switch (status) {
      case 401:
        this.userLoginService.navigateToLogin();
        break;
      case 403: {
        const email = this.userLoginService.currentUser()?.email ?? 'your account';
        this.loadErrorMessage.set(
          `This report hasn't been shared with ${email}. Ask the person who shared it to share it with the email you sign in with.`,
        );
        break;
      }
      case 404:
        this.loadErrorMessage.set("This report doesn't exist or has been deleted.");
        break;
      default:
        this.loadErrorMessage.set("We couldn't load this report. Please try again.");
    }
  }

  share(shareData: Partial<ShareCasesRequest>) {
    this.isSharing.set(true);

    this.reportService
      .shareReport(this.reportId(), {
        emails: shareData.emails ?? [],
        message: shareData.message ?? '',
        to_guests: shareData.to_guests ?? false,
        cc_sender: shareData.cc_sender ?? false,
      })
      .pipe(finalize(() => this.isSharing.set(false)))
      .subscribe({
        next: () => {
          this.report.update((r) => ({
            ...r,
            is_shared: true,
          }));

          this.notifyService.notify('Report shared successfully.');
        },
        error: () => {
          this.notifyService.notify('Failed to share report.');
        },
      });
  }
}
